import React, { ComponentProps, useMemo, useRef } from 'react';

import { useIsContentOverflowing } from '@adsk/alloy-react-helpers';
import Tooltip from '@adsk/alloy-react-tooltip';

import MenuItem, { MenuOptionProps } from './MenuItem';

type TruncatedMenuItemProps = Omit<MenuOptionProps, 'tooltipProps'> & {
  tooltipProps?: Partial<ComponentProps<typeof Tooltip>>;
  inputValue?: string;
  enableSearchHighlight?: boolean;
};

/**
 * A MenuItem that shows a tooltip when the text or subtext overflows.
 * Uses CSS-based truncation (text-overflow: ellipsis) for performance.
 *
 * MenuItem already has CSS truncation applied via ${theme.typography.ellipsis}
 * This component only adds overflow detection and tooltip behavior.
 */
export const TruncatedMenuItem = React.forwardRef<
  HTMLLIElement,
  TruncatedMenuItemProps
>(
  (
    {
      tooltipProps,
      text: textProp,
      subtext: subtextProp,
      inputValue: _,
      enableSearchHighlight: __,
      ...props
    },
    ref,
  ) => {
    const textRef = useRef<HTMLDivElement | null>(null);
    const subtextRef = useRef<HTMLDivElement | null>(null);

    // Detect if text or subtext is overflowing using CSS measurements
    const isTextOverflowing = useIsContentOverflowing(textRef);
    const isSubtextOverflowing = useIsContentOverflowing(subtextRef);

    const showTooltip =
      (typeof textProp === 'string' && isTextOverflowing) ||
      (typeof subtextProp === 'string' && isSubtextOverflowing);

    const combinedTooltipProps = useMemo(
      () =>
        showTooltip
          ? ({
              ...tooltipProps,
              content: (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 'bold' }}>{textProp}</span>
                  <span>{subtextProp}</span>
                </div>
              ),
              placement: 'right',
            } as ComponentProps<typeof Tooltip>)
          : undefined,
      [showTooltip, subtextProp, textProp, tooltipProps],
    );

    return (
      <MenuItem
        {...props}
        ref={ref}
        textRef={textRef}
        subtextRef={subtextRef}
        tooltipProps={combinedTooltipProps}
        text={textProp}
        subtext={subtextProp}
      />
    );
  },
);

TruncatedMenuItem.displayName = 'TruncatedMenuItem';

export default TruncatedMenuItem;
